import React from 'react'
import {
  SessionProvider as NextAuthProvider,
  useSession,
} from 'next-auth/react'
import { Session } from 'next-auth'
import { useRouter } from 'next/router'

import { analytics } from '@gaia-family/dom'

/**
 * Analytics context
 */
export const Analytics = React.createContext(analytics)

/**
 * Abstract away analytics tracking and bind to router events for page
 * analytics
 */
const AnalyticsProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const router = useRouter()

  // when we mount into the browser then create an event binding to route events
  // to track each change as a page event
  React.useEffect(() => {
    const handleRouteChange = () => analytics.page()

    router.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [router.events])

  return <Analytics.Provider value={analytics}>{children}</Analytics.Provider>
}

/**
 * Provide an api and information on user authentication
 */
const AuthProvider: React.FC<{
  children: React.ReactNode
  session?: Session
}> = ({ children, session }) => (
  <NextAuthProvider session={session}>{children}</NextAuthProvider>
)

const UserTracking = ({ children }: { children: React.ReactNode }) => {
  const session = useSession()

  React.useEffect(() => {
    if (session.status === 'authenticated') {
      analytics.identify(session.data.user)
    } else if (session.status === 'unauthenticated') {
      analytics.identify(null)
    }
  }, [session])

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

/**
 * A shell for all apps, standalone or next.js built
 */
export const AppProvider: React.FC<{
  children: React.ReactNode
  session?: Session
}> = ({ children, session }) => (
  <AnalyticsProvider>
    <AuthProvider session={session}>
      <UserTracking>{children}</UserTracking>
    </AuthProvider>
  </AnalyticsProvider>
)
